import type { RelatedTemplate } from "AdminPanel/Spine/store/configStore";
import {
  DEFAULT_VAP_ENCODE_LIMITS,
  normalizeVapEncodeLimits,
  type VapEncodeLimits,
} from "AdminPanel/Spine/editor/export/components/export-vap/vapConfig";
import { exportSpineVapVideos } from "AdminPanel/Spine/editor/export/components/export-vap/exportSpineVapVideos";
import { blobToDataURL } from "AdminPanel/Spine/editor/create-avatar/blobToDataURL";
import type { StaticStackItem } from "AdminPanel/Spine/lib/staticLayerStack";
import {
  zIndexForAvatarTemplateBg,
  zIndexForAvatarTemplateCharacterKonva,
} from "AdminPanel/Spine/lib/staticLayerStack";
import { compositeStaticStackToWebpDataUrl } from "AdminPanel/Spine/lib/compositeStaticStackImage";
import { relatedSelectionHasDynamicSpine } from "AdminPanel/Spine/lib/hasDynamicSpineSelection";
import { postFetcher } from "api/swr";
import { makeShortId } from "commons";
import type { MutableRefObject } from "react";
import { buildAvatarSpineOutfit } from "Editor/lib/avatarSpineOutfit";
import { captureCanvasAvatarPayload } from "Editor/utils/captureCanvasAvatarPayload";

type CanvasNode = { toDataURL?: (opts: object) => string } | null;

export type AvatarSpineExportSnapshot = {
  belowSlotRef?: MutableRefObject<{ app?: any; canvas?: any } | null>;
  aboveSlotRef?: MutableRefObject<{ app?: any; canvas?: any } | null>;
  staticImgItems?: StaticStackItem[];
};

export type CreateMixedSpineAvatarParams = {
  tid: string;
  stage: { h: number };
  drawRef: MutableRefObject<CanvasNode> | null;
  charDrawRef?: MutableRefObject<CanvasNode> | null;
  relatedTemplates: RelatedTemplate[];
  selectedSkins: string[];
  selectedToningColors: Record<string, string>;
  currentAnimation?: string;
  avatarSpineExport?: AvatarSpineExportSnapshot | null;
  encodeLimits?: Partial<VapEncodeLimits> | null;
  name?: string;
  desc?: string;
  /** 1 = 默认套装 */
  defaultSet?: number;
  /** 普通静态模版画幅：0=1:1，1=9:16 */
  fullscreenType?: number;
  onExportProgress?: (progress: number) => void;
  /** 开始上传资源前（视频或静态图） */
  onUploadStart?: () => void;
};

export type CreateMixedSpineAvatarResult = {
  id: string;
  /** video = 编了 VAP；image = 仅静态 webp */
  mode: "video" | "image";
};

function buildMixedStaticItems(params: {
  stageH: number;
  drawRef: MutableRefObject<CanvasNode> | null;
  charDrawRef?: MutableRefObject<CanvasNode> | null;
  avatarSpineExport?: AvatarSpineExportSnapshot | null;
}): StaticStackItem[] {
  const { stageH, drawRef, charDrawRef, avatarSpineExport } = params;
  const exp = avatarSpineExport || {};
  const staticItems: StaticStackItem[] = [...(exp.staticImgItems || [])];
  const pixelRatio = 512 / stageH;
  if (drawRef?.current?.toDataURL) {
    staticItems.push({
      kind: "img",
      key: "avatar-konva-bg",
      url: drawRef.current.toDataURL({ mimeType: "image/png", pixelRatio }),
      zIndex: zIndexForAvatarTemplateBg(),
    });
  }
  if (charDrawRef?.current?.toDataURL) {
    staticItems.push({
      kind: "img",
      key: "avatar-konva-char",
      url: charDrawRef.current.toDataURL({ mimeType: "image/png", pixelRatio }),
      zIndex: zIndexForAvatarTemplateCharacterKonva(),
    });
  }
  return staticItems;
}

/**
 * 普通模版 + 关联 Spine 的混合 avatar 创建。
 * - 选中动态 Spine：双 drawData + VAP
 * - 仅静态关联：双 drawData + 静态 webp（不编视频）
 */
export async function createMixedSpineAvatar(
  params: CreateMixedSpineAvatarParams,
): Promise<CreateMixedSpineAvatarResult> {
  const {
    tid,
    stage,
    drawRef,
    charDrawRef,
    relatedTemplates,
    selectedSkins,
    selectedToningColors,
    currentAnimation,
    avatarSpineExport,
    encodeLimits,
    name,
    desc,
    defaultSet,
    fullscreenType,
    onExportProgress,
  } = params;

  const captured = captureCanvasAvatarPayload(
    tid,
    stage,
    drawRef,
    fullscreenType,
  );
  if (captured && "error" in captured && captured.error === "draft") {
    throw new Error("Cannot upload draft layer");
  }
  const drawData =
    captured && "drawData" in captured ? captured.drawData : null;
  if (!drawData) {
    throw new Error("无法获取画布数据");
  }

  const spineOutfit = buildAvatarSpineOutfit(
    relatedTemplates,
    selectedSkins,
    selectedToningColors,
    currentAnimation,
  );
  if (!spineOutfit) {
    throw new Error("未选择关联 Spine 组件");
  }

  const staticItems = buildMixedStaticItems({
    stageH: stage.h,
    drawRef,
    charDrawRef,
    avatarSpineExport,
  });
  const hasDynamic = relatedSelectionHasDynamicSpine(
    selectedSkins,
    relatedTemplates,
  );
  const id = makeShortId(32);
  const defaultSetFields =
    defaultSet !== undefined && defaultSet !== null
      ? { defaultSet: Number(defaultSet) }
      : {};

  if (!hasDynamic) {
    onExportProgress?.(0.3);
    const imageDataUrl = await compositeStaticStackToWebpDataUrl(staticItems);
    onExportProgress?.(0.7);
    params.onUploadStart?.();
    const uploadRes = await postFetcher(`/spine-avatars/${id}/upload-avatar`, {
      arg: { image: imageDataUrl },
    });
    await postFetcher("/avatars/spine-create", {
      arg: {
        id,
        tid,
        name: name?.trim() || undefined,
        desc: desc?.trim() || undefined,
        drawData: JSON.stringify(drawData),
        spineDrawData: JSON.stringify(spineOutfit),
        url: uploadRes.url,
        bucket: uploadRes.bucket,
        ...defaultSetFields,
      },
    });
    return { id, mode: "image" };
  }

  const exp = avatarSpineExport || {};
  const below = exp.belowSlotRef?.current || {};
  const above = exp.aboveSlotRef?.current || {};
  const limits = normalizeVapEncodeLimits(
    encodeLimits ?? DEFAULT_VAP_ENCODE_LIMITS,
  );

  const { original, preview } = await exportSpineVapVideos({
    app: below.app || null,
    spineCanvas: below.canvas || null,
    spineAboveApp: above.app || null,
    spineAboveCanvas: above.canvas || null,
    staticStackItems: staticItems,
    encodeLimits: limits,
    onProgress: onExportProgress,
  });

  params.onUploadStart?.();

  const [originalDataUrl, previewDataUrl] = await Promise.all([
    blobToDataURL(original),
    blobToDataURL(preview),
  ]);

  const videoUploadRes = await postFetcher(`/spine-avatars/${id}/upload-video`, {
    arg: { video: originalDataUrl },
  });
  const previewUploadRes = await postFetcher(
    `/spine-avatars/${id}/upload-video-preview`,
    { arg: { video: previewDataUrl } },
  );

  await postFetcher("/avatars/spine-create", {
    arg: {
      id,
      tid,
      name: name?.trim() || undefined,
      desc: desc?.trim() || undefined,
      drawData: JSON.stringify(drawData),
      spineDrawData: JSON.stringify(spineOutfit),
      videoUrl: videoUploadRes.videoUrl,
      videoPreviewUrl: previewUploadRes.videoPreviewUrl,
      bucket: videoUploadRes.bucket,
      ...defaultSetFields,
    },
  });

  return { id, mode: "video" };
}
